import type {CopilotMarkdownExtension, ReactComponentsExtension} from '@github-ui/copilot-markdown/extension'
import {dataAttrToPropName, parseJsonAttribute} from '@github-ui/copilot-markdown/utils'
import {visit} from 'unist-util-visit'

import {FileBlock, type FileBlockProps} from './FileBlock'

const fileblockAttribute = 'data-fileblock-props'
const fileblockProperty = dataAttrToPropName(fileblockAttribute)

/** Matches code block meta in the form `name=filename`. The presence of the `name` indicates a file. */
const nameRegex = /^name=(?<name>[^\s]+)/
/**
 * Matches the file name in the first line of a code block.
 * We have this because sometimes the model does it this way, even though it shouldn't.
 */
const fileNameCodeRegex = /^name=(?<name>[^\s]+)\n?/

/** Matches URL in the code block meta in the form `url=url`. */
const urlRegex = /url=(?<url>[^\s]+)/

/** Number of lines of code to show in the block. */
const previewSizeLines = 6

function getName(meta: string | undefined, code: string): {name?: string; code: string} {
  const nameMatch = meta && nameRegex.exec(meta)
  if (nameMatch) return {name: nameMatch.groups?.name, code}

  const codeMatch = fileNameCodeRegex.exec(code)
  if (codeMatch) return {name: codeMatch.groups?.name, code: code.replace(fileNameCodeRegex, '')}

  return {code}
}

function getUrl(meta: string | undefined): string | undefined {
  if (!meta) return undefined

  const urlMatch = urlRegex.exec(meta)
  return urlMatch?.groups?.url
}

const reactComponents: ReactComponentsExtension = {
  code: (props, fallthrough) => {
    const fileblockProps = parseJsonAttribute<FileBlockProps>(props, fileblockAttribute)
    if (!fileblockProps) return fallthrough

    return <FileBlock {...fileblockProps}>{props.children}</FileBlock>
  },
}

/**
 * Renders small blocks that can be clicked to open files for previewing. Must be configured before
 * `codeBlocksExtension` because it also operates on code blocks and should take priority.
 */
export default function fileViewExtension(fileBlocksInMessage: string[]): CopilotMarkdownExtension {
  return {
    transformMarkdown: tree => {
      visit(tree, 'code', node => {
        const {name, code} = getName(node.meta ?? undefined, node.value)
        if (!name) return

        const fileLanguage = node.lang ?? ''
        const fileCode = code

        if (!fileBlocksInMessage.includes(name)) {
          fileBlocksInMessage.push(name)
        }

        const url = getUrl(node.meta ?? undefined)
        const previewLines = fileCode.split('\n', previewSizeLines)

        // When clipped, the last line is faded out so the padding is not needed. When not clipped, tack on an extra newline for padding
        const isClipped = previewLines.length === previewSizeLines
        const previewCode = isClipped ? previewLines.join('\n') : `${previewLines.join('\n')}\n`

        const lineNumber = node.position?.start.line ?? 0

        // Clear node.lang for mermaid blocks to prevent the mermaid
        // extension from overwriting the file block data set below.
        if (fileLanguage === 'mermaid') {
          node.lang = undefined
        }
        node.value = previewCode
        node.data = {
          ...node.data,
          hName: 'code',
          hProperties: {
            [fileblockProperty]: JSON.stringify({
              language: fileLanguage,
              name,
              url,
              code: fileCode,
              line: lineNumber,
              isStreaming: node.isStreaming ?? false,
              isClipped,
              index: fileBlocksInMessage.indexOf(name),
              // Every component will receive a node. This is the original Element from hast element being turned into a React element.
              // see https://github.com/remarkjs/react-markdown/tree/main?tab=readme-ov-file#appendix-b-components
              startOffset: node.position?.start.offset ?? -1,
              endOffset: node.position?.end.offset ?? -1,
            } satisfies FileBlockProps),
          },
        }
      })
    },
    transformHtml: tree =>
      // Code nodes render as two nested elements (pre > code) by default, so we transform the html to remove the outer element
      visit(tree, 'element', (node, i, parent) => {
        const child = node.children?.[0]
        if (
          parent &&
          i !== undefined &&
          node.tagName === 'pre' &&
          node.children?.length === 1 &&
          child?.type === 'element' &&
          child.tagName === 'code' &&
          fileblockProperty in child.properties
        )
          parent.children.splice(i, 1, child)
      }),
    reactComponents,
  }
}
